import React, { useContext, useEffect, useId, useMemo, type PropsWithChildren } from 'react';
import { useEntitySecurityChain, useEntitySecurityPath, useFeatureSecurityChain } from '../../auth/Security';
import { useDeepLinkRuntimeState } from '../../deepLink';
import { SIDE_PANEL_CLOSE_REASONS } from '../constants';
import type { SidePanelDeclaration } from '../types';
import { SidePanelOwnerContext } from './contexts/SidePanelOwnerContext';
import { useInternalSidePanelSystem } from './hooks/useInternalSidePanelSystem';
import { contributionsAtom, layoutAtom, sessionAtom } from './state/atoms';
import { dispatchOwnerNotification } from './state/dispatchOwnerNotification';

export type SidePanelOwnerProps = PropsWithChildren<{
    panels: readonly SidePanelDeclaration[];
}>;

export function SidePanelOwner({ panels, children }: SidePanelOwnerProps) {
    const ownerId = useId();
    const parentOwner = useContext(SidePanelOwnerContext);
    const { store, commandHandlers } = useInternalSidePanelSystem();
    const entitySecurityChain = useEntitySecurityChain();
    const featureSecurityChain = useFeatureSecurityChain();
    const deepLinkBasePath = useEntitySecurityPath();
    const { clearDeepLinkRuntimeState } = useDeepLinkRuntimeState();

    const ownerValue = useMemo(() => {
        const registration = {
            ownerId,
            declarations: panels,
            entitySecurityChain,
            featureSecurityChain,
            deepLinkBasePath
        };

        return {
            ...registration,
            ownerChain: [ownerId, ...(parentOwner?.ownerChain ?? [])],
            ownerRegistrations: [registration, ...(parentOwner?.ownerRegistrations ?? [])]
        };
    }, [
        deepLinkBasePath,
        entitySecurityChain,
        featureSecurityChain,
        ownerId,
        panels,
        parentOwner?.ownerChain,
        parentOwner?.ownerRegistrations
    ]);

    // Publish this owner's declarations and remove them on cleanup; declaration changes do not close its session.
    useEffect(() => {
        store.set(contributionsAtom, (current) => new Map(current).set(ownerId, panels));

        return () => {
            store.set(contributionsAtom, (current) => {
                const next = new Map(current);
                next.delete(ownerId);
                return next;
            });
        };
    }, [ownerId, panels, store]);

    // Separately tie session lifetime to the declaring owner: cleanup closes only this owner's active session,
    // clears its runtime deep link, resets expansion, and notifies the captured owner chain.
    useEffect(
        () => () => {
            const session = store.get(sessionAtom);
            if (session?.ownerId !== ownerId) {
                return;
            }
            if (session.deepLinkPath) {
                clearDeepLinkRuntimeState(session.deepLinkPath);
            }
            dispatchOwnerNotification(
                session,
                'panel.closed',
                { panelId: session.panelId, reason: SIDE_PANEL_CLOSE_REASONS.ownerUnmounted },
                commandHandlers
            );
            store.set(sessionAtom, null);
            store.set(layoutAtom, { isExpanded: false });
        },
        [clearDeepLinkRuntimeState, commandHandlers, ownerId, store]
    );

    return <SidePanelOwnerContext.Provider value={ownerValue}>{children}</SidePanelOwnerContext.Provider>;
}
